/**
 * Contract Section Renderer Components
 *
 * React components for rendering different types of contract content
 */

import React from 'react';
import type {
  SectionContent,
  HeadingContent,
  ParagraphContent,
  ListContent,
  TableContent,
  CheckboxListContent,
  SignatureFieldContent,
  DividerContent
} from './contract-types';

// ============================================================================
// Individual Content Renderers
// ============================================================================

/**
 * Heading renderer
 */
export const HeadingRenderer: React.FC<{ content: HeadingContent }> = ({ content }) => {
  const { text, level } = content;

  const className = {
    1: 'text-2xl font-bold text-gray-900 mb-6 mt-8 text-center break-words max-w-full',
    2: 'text-xl font-bold text-gray-900 mb-4 mt-6 break-words max-w-full',
    3: 'text-lg font-semibold text-gray-800 mb-3 mt-4 break-words max-w-full',
    4: 'text-base font-semibold text-gray-700 mb-2 mt-3 break-words max-w-full'
  }[level];

  const Tag = `h${level}` as keyof JSX.IntrinsicElements;

  return <Tag className={className}>{text}</Tag>;
};

/**
 * Paragraph renderer
 */
export const ParagraphRenderer: React.FC<{ content: ParagraphContent }> = ({ content }) => {
  return (
    <p className="text-sm text-gray-700 leading-relaxed mb-3 break-words max-w-full">
      {content.text}
    </p>
  );
};

/**
 * List renderer (numbered or bulleted)
 */
export const ListRenderer: React.FC<{ content: ListContent }> = ({ content }) => {
  const { type, items } = content;

  const ListTag = type === 'numbered_list' ? 'ol' : 'ul';
  const listClassName = type === 'numbered_list'
    ? 'list-decimal list-inside space-y-2 mb-4 max-w-full'
    : 'list-disc list-inside space-y-2 mb-4 max-w-full';

  return (
    <ListTag className={listClassName}>
      {items.map((item, index) => (
        <li key={index} className="text-sm text-gray-700 leading-relaxed pl-2 break-words">
          {item}
        </li>
      ))}
    </ListTag>
  );
};

/**
 * Table renderer
 */
export const TableRenderer: React.FC<{ content: TableContent }> = ({ content }) => {
  const { headers, rows } = content;

  return (
    <div className="overflow-x-auto mb-4">
      <table className="min-w-full border border-gray-300">
        {headers && headers.length > 0 && (
          <thead>
            <tr className="bg-gray-100">
              {headers.map((header, index) => (
                <th
                  key={index}
                  className="px-4 py-2 text-left text-sm font-semibold text-gray-700 border-b border-gray-300"
                >
                  {header}
                </th>
              ))}
            </tr>
          </thead>
        )}
        <tbody>
          {rows.map((row, rowIndex) => (
            <tr
              key={rowIndex}
              className={rowIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50'}
            >
              {row.map((cell, cellIndex) => (
                <td
                  key={cellIndex}
                  className="px-4 py-2 text-sm text-gray-700 border-b border-gray-200"
                >
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

/**
 * Checkbox list renderer
 */
export const CheckboxListRenderer: React.FC<{
  content: CheckboxListContent;
  checkboxStates?: Record<string, boolean>;
  onCheckboxChange?: (id: string, checked: boolean) => void;
  readOnly?: boolean;
}> = ({ content, checkboxStates = {}, onCheckboxChange, readOnly = false }) => {
  const { items } = content;

  return (
    <div className="space-y-3 mb-4">
      {items.map((item) => {
        const isChecked = checkboxStates[item.id] ?? item.checked ?? false;

        return (
          <div key={item.id} className="flex items-start gap-3">
            <input
              type="checkbox"
              id={item.id}
              checked={isChecked}
              onChange={(e) => onCheckboxChange?.(item.id, e.target.checked)}
              disabled={readOnly}
              className="mt-1 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:opacity-50"
            />
            <label
              htmlFor={item.id}
              className="text-sm text-gray-700 leading-relaxed cursor-pointer select-none"
            >
              {item.label}
            </label>
          </div>
        );
      })}
    </div>
  );
};

/**
 * Signature field renderer
 */
export const SignatureFieldRenderer: React.FC<{
  content: SignatureFieldContent;
}> = ({ content }) => {
  const { label } = content;

  return (
    <div className="mb-4">
      <div className="text-sm font-medium text-gray-700 mb-2">{label}:</div>
      <div className="border-b-2 border-gray-400 w-full h-12 mb-2"></div>
    </div>
  );
};

/**
 * Divider renderer
 */
export const DividerRenderer: React.FC<{ content: DividerContent }> = () => {
  return <hr className="my-6 border-t-2 border-gray-300" />;
};

// ============================================================================
// Main Content Renderer (Switch)
// ============================================================================

/**
 * Main content renderer that switches based on content type
 */
export const ContentRenderer: React.FC<{
  content: SectionContent;
  checkboxStates?: Record<string, boolean>;
  onCheckboxChange?: (id: string, checked: boolean) => void;
  readOnly?: boolean;
}> = ({ content, checkboxStates, onCheckboxChange, readOnly }) => {
  switch (content.type) {
    case 'heading':
      return <HeadingRenderer content={content} />;

    case 'paragraph':
      return <ParagraphRenderer content={content} />;

    case 'numbered_list':
    case 'bulleted_list':
      return <ListRenderer content={content} />;

    case 'table':
      return <TableRenderer content={content} />;

    case 'checkbox_list':
      return (
        <CheckboxListRenderer
          content={content}
          checkboxStates={checkboxStates}
          onCheckboxChange={onCheckboxChange}
          readOnly={readOnly}
        />
      );

    case 'signature_field':
      return <SignatureFieldRenderer content={content} />;

    case 'divider':
      return <DividerRenderer content={content} />;

    default:
      console.warn('Unknown content type:', (content as any).type);
      return null;
  }
};

// ============================================================================
// Section Renderer
// ============================================================================

/**
 * Render a complete contract section with all its content
 */
export const SectionRenderer: React.FC<{
  section: {
    id: string;
    title: string;
    content: SectionContent[];
  };
  variableData: Record<string, any>;
  checkboxStates?: Record<string, boolean>;
  onCheckboxChange?: (checkboxId: string, checked: boolean) => void;
  readOnly?: boolean;
  interpolate: (text: string, variables: Record<string, any>) => string;
}> = ({
  section,
  variableData,
  checkboxStates,
  onCheckboxChange,
  readOnly = true,
  interpolate
}) => {
  return (
    <div className="contract-section mb-8" id={section.id}>
      {section.content.map((contentItem) => {
        // Check conditional display rules
        if ('conditionalDisplay' in contentItem && contentItem.conditionalDisplay) {
          const { field, operator } = contentItem.conditionalDisplay as { field: string; operator: string };
          const fieldValue = variableData[field];

          // Apply conditional logic
          if (operator === 'isNotEmpty') {
            if (!fieldValue || fieldValue === '' || (Array.isArray(fieldValue) && fieldValue.length === 0)) {
              return null; // Skip rendering this content item
            }
          }
          // Add more operators as needed (equals, notEquals, greaterThan, etc.)
        }

        // Interpolate variables in text content
        let processedContent = { ...contentItem };

        if ('text' in processedContent) {
          processedContent = {
            ...processedContent,
            text: interpolate(processedContent.text, variableData)
          };
        }

        if ('items' in processedContent && Array.isArray(processedContent.items)) {
          if (typeof processedContent.items[0] === 'string') {
            // List items
            processedContent = {
              ...processedContent,
              items: processedContent.items.map((item) =>
                typeof item === 'string' ? interpolate(item, variableData) : item
              )
            };
          } else if (typeof processedContent.items[0] === 'object') {
            // Checkbox items
            processedContent = {
              ...processedContent,
              items: processedContent.items.map((item: any) => ({
                ...item,
                label: interpolate(item.label, variableData)
              }))
            };
          }
        }

        if ('rows' in processedContent && Array.isArray(processedContent.rows)) {
          // Table rows
          processedContent = {
            ...processedContent,
            rows: processedContent.rows.map((row) =>
              row.map((cell) => interpolate(cell, variableData))
            )
          };
        }

        if ('headers' in processedContent && Array.isArray(processedContent.headers)) {
          // Table headers
          processedContent = {
            ...processedContent,
            headers: processedContent.headers.map((header) =>
              interpolate(header, variableData)
            )
          };
        }

        return (
          <ContentRenderer
            key={contentItem.id}
            content={processedContent}
            checkboxStates={checkboxStates}
            onCheckboxChange={onCheckboxChange}
            readOnly={readOnly}
          />
        );
      })}
    </div>
  );
};
